import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, Eye, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useCms } from "@/hooks/useCms";
import { FlagIcon } from "@/components/FlagIcon";
import { InteractiveWorldMap } from "@/components/InteractiveWorldMap";

const DEFAULT_GLOBAL_PRESENCE = [
  { flag: "🇩🇪", country: "Germany", addr: "Unterseilberg 9, 94143, Grainet, Bayern" },
  { flag: "🇺🇸", country: "USA", addr: "20270 Plymouth Rd, Detroit, MI 48228" },
  { flag: "🇨🇦", country: "Canada", addr: "1315 Finch Avenue West, Toronto ON M3J 2G6" },
  { flag: "🇬🇧", country: "UK", addr: "1A Town Square, Erith, Kent DA8 1RE" },
  { flag: "🇳🇬", country: "Nigeria", addr: "95 Ogudu Road, Ogudu, Lagos" },
];

const DEFAULT_CORE_VALUES = [
  { emoji: "🏆", name: "Excellence", desc: "Striving for the highest standard in athlete development." },
  { emoji: "🤝", name: "Integrity", desc: "Honest, fair, and transparent in all dealings." },
  { emoji: "🌍", name: "Global Vision", desc: "Thinking internationally, acting locally." },
  { emoji: "❤️", name: "Passion", desc: "Deep love for sports drives everything we do." },
];

export default function About() {
  const { getText, getMeta } = useCms();

  const storyContent = getText("about_story", "ELIADA SPORTS AGENCY is an international sports management and talent development organization committed to discovering, nurturing, and promoting exceptional football and sports talents across the world.\n\nWith strong operational networks in Africa, Europe, and North America, the agency serves as the most trusted global platform that connects talented footballers and athletes with professional opportunities, training institutions, clubs, and investors within the international sports industry.\n\nWe were founded with the vision of creating a structured pathway for young athletes. By combining talent identification, professional training, mentorship, financial support, and international exposure, we empower athletes to reach their full potential both on and off the field.");
  const storyMeta = getMeta<{ image?: string }>("about_story", { image: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?q=80&w=2000&auto=format&fit=crop" });
  const storyImage = storyMeta.image || "https://images.unsplash.com/photo-1517466787929-bc90951d0974?q=80&w=2000&auto=format&fit=crop";

  const visionText = getText("about_vision", "To become a globally recognized sports talent development and management organization that transforms young talents into internationally successful professional athletes.");
  const missionText = getText("about_mission", "To discover, develop, and promote sports talents through structured training, international exposure, mentorship, and career management, ensuring every athlete achieves their maximum potential.");

  const FALLBACK_HERO = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1600&auto=format&fit=crop";
  const heroMeta = getMeta<{ image?: string }>("about_hero", { image: FALLBACK_HERO });
  const heroImage = (heroMeta.image && heroMeta.image.startsWith("http")) ? heroMeta.image : FALLBACK_HERO;
  const heroSubtitle = getText("about_hero", "Discovering, nurturing, and promoting exceptional talents across the world.");

  const globalPresence = getMeta<{ items: typeof DEFAULT_GLOBAL_PRESENCE }>("about_global_presence", { items: DEFAULT_GLOBAL_PRESENCE }).items ?? DEFAULT_GLOBAL_PRESENCE;
  const coreValues = getMeta<{ items: typeof DEFAULT_CORE_VALUES }>("about_core_values", { items: DEFAULT_CORE_VALUES }).items ?? DEFAULT_CORE_VALUES;

  const commitmentMeta = getMeta<{ pillars?: string[]; description?: string }>("about_commitment", {
    pillars: ["DISCOVER TALENT", "DEVELOP ATHLETES", "CREATE OPPORTUNITIES", "BUILD CAREERS"],
    description: "Through professionalism, integrity, and passion for sports, the agency continues to shape the future of young athletes and contribute to the growth of the international sports industry.",
  });

  const globalPresenceSubtitle = getText("about_global_presence", "We maintain active operations and partnerships across multiple continents to serve our athletes locally and globally.");
  const coreValuesHeadline = getText("about_core_values", "OUR CORE VALUES");

  return (
    <>
      <SeoHead
        title="About Us"
        description="Learn about Eliada Sports Agency — our mission to bridge athletic talent with global opportunity, our vision for the future of sports, and the values that drive everything we do."
        path="/about"
      />
      <PageTransition className="pt-24 pb-0">
      <div className="relative py-28 bg-secondary overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src={heroImage.startsWith("http") ? heroImage : `${import.meta.env.BASE_URL}${heroImage}`} alt="" aria-hidden="true" className="w-full h-full object-cover object-center opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
            <ShieldCheck size={13} />
            <span className="text-xs font-display tracking-widest uppercase font-semibold">Eliada Sports Agency</span>
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-5">ABOUT <span className="text-primary">ELIADA SPORTS</span></h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">{heroSubtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-32 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-display font-bold text-white mb-6">OUR STORY</h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              {storyContent.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <img src={storyImage} alt="Professional coaching" className="rounded-2xl shadow-2xl border border-white/10 relative z-10" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary rounded-xl z-0" />
            <div className="absolute -top-6 -right-6 w-32 h-32 border-2 border-primary rounded-xl z-0" />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-32">
          <Card className="bg-gradient-to-br from-card to-background border-primary/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-3xl rounded-full" />
            <CardHeader>
              <Eye className="w-12 h-12 text-primary mb-4" />
              <CardTitle className="text-3xl">Our Vision</CardTitle>
            </CardHeader>
            <CardContent><p className="text-lg leading-relaxed">{visionText}</p></CardContent>
          </Card>
          <Card className="bg-gradient-to-bl from-card to-background border-primary/20 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 blur-3xl rounded-full" />
            <CardHeader>
              <Target className="w-12 h-12 text-primary mb-4" />
              <CardTitle className="text-3xl">Our Mission</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">{missionText}</p>
            </CardContent>
          </Card>
        </div>

        <div className="mb-32">
          <InteractiveWorldMap />
        </div>

        <div className="mb-32">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">{coreValuesHeadline.includes("CORE") ? <>{coreValuesHeadline.split(" ").slice(0, -1).join(" ")} <span className="text-primary">{coreValuesHeadline.split(" ").at(-1)}</span></> : coreValuesHeadline}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-background p-8 rounded-2xl border border-white/10 text-center">
                <div className="text-5xl mb-6">{value.emoji}</div>
                <h3 className="font-display font-bold text-white text-2xl mb-3">{value.name}</h3>
                <p className="text-muted-foreground">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="bg-primary text-primary-foreground rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden shadow-[0_0_50px_rgba(255,191,0,0.15)] mb-24">
          <ShieldCheck className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-8">OUR COMMITMENT</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 font-display text-xl md:text-2xl font-semibold tracking-wide">
            {(commitmentMeta.pillars ?? []).map((p, i) => <div key={i}>{p}</div>)}
          </div>
          <p className="max-w-3xl mx-auto text-lg md:text-xl opacity-90 font-medium leading-relaxed">{commitmentMeta.description}</p>
        </div>
      </div>
      </PageTransition>
    </>
  );
}
